import { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Check, X, Loader2, Download, MessageSquare,
  Clock, Palette, Send, RefreshCw,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "Awaiting Your Review", color: "#946600", bg: "rgba(200,162,74,0.14)" },
  approved: { label: "Approved", color: "#1a4a1a", bg: "rgba(74,124,62,0.18)" },
  revision_requested: { label: "Revision Requested", color: "#8a1e1e", bg: "rgba(155,52,52,0.14)" },
};

const CAT_ICONS = {
  label: Palette, logo: Palette, packaging: FileText,
  mockup: FileText, brand_guidelines: FileText, other: FileText,
};

const PortalArtwork = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/portal/artwork")
      .then((r) => setItems(r.data.artworks || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const pending = useMemo(() => items.filter((a) => a.status === "pending"), [items]);
  const decided = useMemo(() => items.filter((a) => a.status !== "pending"), [items]);

  return (
    <PortalLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Portal</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-artwork-title">
            Artwork Approval Center
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Review every artwork version your FLOWTALIS design team ships. Approve to
            release into production, or request revisions with specific feedback.
          </p>
        </div>
        <button onClick={load} className="ft-btn-ghost" data-testid="portal-artwork-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : items.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="portal-artwork-empty">
          <Palette size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No artwork yet.</h3>
          <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
            Once your FLOWTALIS design team uploads a version, it will appear here for
            your review. You&apos;ll be notified by email + in the portal bell.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {pending.length > 0 && (
            <Section title={`Awaiting Your Review (${pending.length})`} eyebrow="Action Required">
              {pending.map((a) => (
                <ArtCard key={a.id} art={a} onOpen={() => setActive(a)} />
              ))}
            </Section>
          )}
          {decided.length > 0 && (
            <Section title={`History (${decided.length})`} eyebrow="Past Decisions">
              {decided.map((a) => (
                <ArtCard key={a.id} art={a} onOpen={() => setActive(a)} />
              ))}
            </Section>
          )}
        </div>
      )}

      {active && (
        <ReviewDrawer
          art={active}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); load(); }}
        />
      )}
    </PortalLayout>
  );
};

const Section = ({ title, eyebrow, children }) => (
  <div>
    <div className="mb-3">
      <div className="ft-eyebrow">{eyebrow}</div>
      <h2 className="text-lg font-semibold tracking-tight mt-1">{title}</h2>
    </div>
    <div className="grid gap-3">{children}</div>
  </div>
);

const ArtCard = ({ art, onOpen }) => {
  const Icon = CAT_ICONS[art.category] || FileText;
  const meta = STATUS_META[art.status] || STATUS_META.pending;
  return (
    <button
      onClick={onOpen}
      className="ft-card text-left hover:border-[var(--ft-gold)] transition-colors w-full"
      data-testid={`portal-artwork-card-${art.id}`}
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg flex items-center justify-center bg-[var(--ft-gold-soft)] text-[var(--ft-gold)] shrink-0">
            <Icon size={22} />
          </div>
          <div>
            <div className="ft-eyebrow">{(art.category || "other").replace("_", " ")} · v{art.version}</div>
            <h3 className="text-lg font-bold tracking-tight mt-1">{art.title}</h3>
            <div className="text-xs text-[var(--ft-muted)] mt-1">
              {art.brand_name ? `${art.brand_name} · ` : ""}
              Uploaded {new Date(art.created_at).toLocaleDateString()} by {art.uploaded_by}
            </div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: meta.bg, color: meta.color }}
        >
          {meta.label}
        </span>
      </div>
    </button>
  );
};

const ReviewDrawer = ({ art, onClose, onDone }) => {
  const [comment, setComment] = useState("");
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const meta = STATUS_META[art.status] || STATUS_META.pending;
  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;
  const previewSrc = `${API}/portal/artwork/${art.id}/download?auth=${encodeURIComponent(token || "")}`;
  const isImage = (art.content_type || "").startsWith("image/");
  const isPdf = (art.content_type || "") === "application/pdf";

  const submit = async () => {
    if (!decision) return toast.error("Choose Approve or Request Revision");
    if (!comment.trim() || comment.trim().length < 3)
      return toast.error("A short comment is required so your team knows what to do next");
    setSubmitting(true);
    try {
      await api.post(`/portal/artwork/${art.id}/decision`, { decision, comment });
      toast.success(decision === "approved" ? "Approved — moved to Brand Vault." : "Revision requested.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit decision");
    } finally { setSubmitting(false); }
  };

  const done = art.status !== "pending";

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="portal-artwork-drawer">
      <button className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[var(--ft-border)] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="ft-eyebrow">{(art.category || "other").replace("_", " ")} · v{art.version}</div>
            <h3 className="text-lg font-bold tracking-tight">{art.title}</h3>
          </div>
          <button onClick={onClose} className="ft-btn-ghost" data-testid="portal-artwork-drawer-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
              style={{ background: meta.bg, color: meta.color }}
            >
              {meta.label}
            </span>
          </div>

          <div className="rounded-lg bg-[var(--ft-soft-gray)] border border-[var(--ft-border)] overflow-hidden">
            {isImage ? (
              <img src={previewSrc} alt={art.title} className="w-full max-h-[560px] object-contain bg-white" />
            ) : isPdf ? (
              <iframe title={art.title} src={previewSrc} className="w-full h-[560px] bg-white" />
            ) : (
              <div className="p-8 text-center text-sm text-[var(--ft-muted)]">
                Preview unavailable for this file type. Download to view.
              </div>
            )}
          </div>

          <a
            href={previewSrc}
            download={art.original_filename || art.title}
            className="ft-btn-secondary inline-flex"
            data-testid="portal-artwork-download"
          >
            <Download size={14} /> Download original
          </a>

          {done ? (
            <div className="p-4 rounded-lg border" style={{ background: meta.bg, borderColor: meta.color }}>
              <div className="text-[0.7rem] uppercase tracking-[0.22em] font-semibold" style={{ color: meta.color }}>
                {meta.label}
              </div>
              <div className="text-sm mt-1"><strong>Your feedback:</strong> {art.review_comment || "—"}</div>
              <div className="text-xs text-[var(--ft-muted)] mt-1">
                {art.reviewed_by} · {art.reviewed_at ? new Date(art.reviewed_at).toLocaleString() : ""}
              </div>
            </div>
          ) : (
            <div className="ft-card space-y-4" data-testid="portal-artwork-decision-form">
              <div>
                <div className="ft-eyebrow mb-2">Your Decision</div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setDecision("approved")}
                    className={`p-4 rounded-lg border text-left transition-all ${decision === "approved" ? "border-[var(--ft-gold)] bg-[var(--ft-gold-soft)]" : "border-[var(--ft-border)] hover:border-[var(--ft-gold)]"}`}
                    data-testid="portal-artwork-approve-choice"
                  >
                    <Check size={20} className="text-[var(--ft-gold)]" />
                    <div className="font-semibold mt-2">Approve</div>
                    <div className="text-xs text-[var(--ft-muted)]">Release to production &amp; save in your Brand Vault.</div>
                  </button>
                  <button
                    onClick={() => setDecision("revision_requested")}
                    className={`p-4 rounded-lg border text-left transition-all ${decision === "revision_requested" ? "border-[var(--ft-gold)] bg-[var(--ft-gold-soft)]" : "border-[var(--ft-border)] hover:border-[var(--ft-gold)]"}`}
                    data-testid="portal-artwork-revise-choice"
                  >
                    <RefreshCw size={20} className="text-[var(--ft-gold)]" />
                    <div className="font-semibold mt-2">Request Revision</div>
                    <div className="text-xs text-[var(--ft-muted)]">Send it back with specific feedback.</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="ft-label block mb-1.5">
                  {decision === "approved" ? "Approval note (required)" : "Revision feedback (required)"}
                </label>
                <textarea
                  className="ft-input min-h-[110px]"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={decision === "approved"
                    ? "e.g. Looks great — proceed to production."
                    : "e.g. Please make the logo 15% larger and shift the gold accent higher on the label."}
                  data-testid="portal-artwork-comment"
                />
              </div>

              <div className="flex justify-end">
                <button onClick={submit} disabled={submitting} className="ft-btn-primary" data-testid="portal-artwork-submit">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Submit Decision
                </button>
              </div>
            </div>
          )}

          <div>
            <div className="ft-eyebrow mb-2">Activity Timeline</div>
            <ul className="space-y-2">
              {[...(art.activity || [])].reverse().map((a, i) => (
                <li key={i} className="flex gap-2 text-xs">
                  <Clock size={10} className="mt-1 text-[var(--ft-gold)] shrink-0" />
                  <span className="text-[var(--ft-muted)]">{new Date(a.at).toLocaleString()}</span>
                  <span className="text-[var(--ft-charcoal)]">
                    <strong>{a.actor}</strong> · {a.detail}
                  </span>
                </li>
              ))}
              {(!art.activity || art.activity.length === 0) && (
                <li className="text-xs text-[var(--ft-muted)]"><MessageSquare size={12} className="inline mr-1" /> No activity yet.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalArtwork;
